import { useCallback, useEffect, useState } from "react";
import { Clock, LogIn, LogOut, CalendarDays, Users, Settings2, X, MapPin } from "lucide-react";
import { toast } from "sonner";
import { API, getGeoPosition } from "@/api";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "—");
const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", weekday: "short" });

const REMARK_STYLE = { Late: "text-rose-600", HalfDay: "text-amber-600", LeftEarly: "text-orange-600" };

function RemarkCell({ e }) {
  const remark = e.remark || (e.late ? "Late" : "");
  if (!remark) return <span className="text-slate-300">—</span>;
  return (
    <span className="text-xs font-semibold" data-testid={`remark-${e.id}`}>
      {remark.split("-").map((p, i) => (
        <span key={p}>
          {i > 0 && <span className="text-slate-300">-</span>}
          <span className={REMARK_STYLE[p.trim()] || "text-slate-600"}>{p}</span>
        </span>
      ))}
    </span>
  );
}

function EntriesTable({ entries, showEmployee, testid }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {showEmployee && <TableHead>Employee</TableHead>}
          <TableHead>Date</TableHead>
          <TableHead className="text-right">Clock In</TableHead>
          <TableHead className="text-right">Clock Out</TableHead>
          <TableHead className="text-right">Remark</TableHead>
          <TableHead className="text-right">Hours</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody data-testid={testid}>
        {entries.map((e) => (
          <TableRow key={e.id} data-testid={`attendance-row-${e.id}`}>
            {showEmployee && <TableCell className="font-medium text-slate-900">{e.employee}</TableCell>}
            <TableCell>{fmtDate(e.date)}</TableCell>
            <TableCell className="text-right">
              {fmtTime(e.clock_in)}
              {e.geo?.lat != null && (
                <a href={`https://www.google.com/maps?q=${e.geo.lat},${e.geo.lng}`} target="_blank" rel="noreferrer" title="Clock-in location" data-testid={`geo-link-${e.id}`}>
                  <MapPin className="ml-1 inline h-3.5 w-3.5 text-blue-500 hover:text-blue-700" />
                </a>
              )}
            </TableCell>
            <TableCell className="text-right">{fmtTime(e.clock_out)}</TableCell>
            <TableCell className="text-right"><RemarkCell e={e} /></TableCell>
            <TableCell className="text-right">{e.hours ? `${e.hours}h` : "—"}</TableCell>
          </TableRow>
        ))}
        {entries.length === 0 && (
          <TableRow><TableCell colSpan={showEmployee ? 6 : 5}><EmptyState compact icon={CalendarDays} title="No attendance entries for this month" testid="attendance-entries-empty" /></TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  );
}

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [month, setMonth] = useState(thisMonth());
  const [today, setToday] = useState(null);
  const [myEntries, setMyEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [summary, setSummary] = useState(null);
  const [empSel, setEmpSel] = useState("");
  const [empEntries, setEmpEntries] = useState([]);
  const [cfg, setCfg] = useState({ shift_start: "", shift_end: "", per_user: {}, per_user_end: {}, working_days: [0, 1, 2, 3, 4, 5], alt_sundays: true, holidays: [], geo_lat: "", geo_lng: "", geo_radius_m: 200 });
  const [hol, setHol] = useState({ date: "", name: "" });
  const addHoliday = () => {
    if (!hol.date || !hol.name.trim()) return toast.error("Pick a date and name the holiday");
    setCfg({ ...cfg, holidays: [...cfg.holidays.filter((h) => h.date !== hol.date), { date: hol.date, name: hol.name.trim() }].sort((a, b) => a.date.localeCompare(b.date)) });
    setHol({ date: "", name: "" });
  };
  const removeHoliday = (d) => setCfg({ ...cfg, holidays: cfg.holidays.filter((h) => h.date !== d) });
  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not available on this device");
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setCfg((c) => ({ ...c, geo_lat: p.coords.latitude.toFixed(6), geo_lng: p.coords.longitude.toFixed(6) }));
        toast.success("Office location captured — Save to apply");
      },
      () => toast.error("Location permission denied"),
      { timeout: 8000 }
    );
  };
  const [savingCfg, setSavingCfg] = useState(false);

  const load = useCallback(() => {
    API.get("/attendance/today").then((r) => setToday(r.data)).catch(() => {});
    API.get("/attendance/my", { params: { month } }).then((r) => setMyEntries(r.data)).catch(() => {});
    if (isAdmin) {
      API.get("/attendance/employees").then((r) => setEmployees(r.data)).catch(() => {});
      API.get("/attendance/summary", { params: { month } }).then((r) => setSummary(r.data)).catch(() => {});
      API.get("/attendance/config").then((r) => setCfg({
        shift_start: r.data.shift_start || "",
        shift_end: r.data.shift_end || "",
        per_user: r.data.per_user || {},
        per_user_end: r.data.per_user_end || {},
        working_days: r.data.working_days?.length ? r.data.working_days : [0, 1, 2, 3, 4, 5],
        alt_sundays: r.data.alt_sundays !== false,
        holidays: r.data.holidays || [],
        geo_lat: r.data.geo_lat ?? "",
        geo_lng: r.data.geo_lng ?? "",
        geo_radius_m: r.data.geo_radius_m || 200,
      })).catch(() => {});
    }
  }, [month, isAdmin]);
  useEffect(load, [load]);
  useEffect(() => {
    const refresh = () => load();
    window.addEventListener("tf-attendance-changed", refresh);
    return () => window.removeEventListener("tf-attendance-changed", refresh);
  }, [load]);

  useEffect(() => {
    if (!empSel) { setEmpEntries([]); return; }
    API.get("/attendance/all", { params: { month, user_id: empSel } }).then((r) => setEmpEntries(r.data)).catch(() => {});
  }, [empSel, month]);

  const punch = async (kind) => {
    setBusy(true);
    try {
      const body = kind === "in" ? (await getGeoPosition()) || {} : {};
      await API.post(`/attendance/clock-${kind}`, body);
      toast.success(kind === "in" ? "Clocked in — have a great day!" : "Clocked out — see you tomorrow!");
      window.dispatchEvent(new Event("tf-attendance-changed"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const per_user = Object.fromEntries(Object.entries(cfg.per_user).filter(([, v]) => v));
      const per_user_end = Object.fromEntries(Object.entries(cfg.per_user_end).filter(([, v]) => v));
      await API.put("/attendance/config", {
        shift_start: cfg.shift_start,
        shift_end: cfg.shift_end,
        per_user,
        per_user_end,
        working_days: cfg.working_days,
        alt_sundays: cfg.alt_sundays,
        holidays: cfg.holidays,
        geo_lat: cfg.geo_lat === "" ? null : Number(cfg.geo_lat),
        geo_lng: cfg.geo_lng === "" ? null : Number(cfg.geo_lng),
        geo_radius_m: Number(cfg.geo_radius_m) || 200,
      });
      toast.success("Shift settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingCfg(false);
    }
  };

  const entry = today?.entry;
  const open = entry && !entry.clock_out;

  return (
    <div className="max-w-5xl space-y-6" data-testid="attendance-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Attendance</h1>
        <p className="text-sm text-slate-500">Company 2 team — clock in/out and monthly records (times in IST)</p>
      </div>

      <Card className="border-slate-200" data-testid="today-card">
        <CardContent className="flex flex-wrap items-center justify-between gap-4 pt-6">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50">
              <Clock className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900" data-testid="today-status">
                {!entry ? "Not clocked in yet" : open ? `Clocked in at ${fmtTime(entry.clock_in)}` : `Done for today — ${entry.hours}h`}
              </div>
              <div className="text-xs text-slate-500">
                {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric", timeZone: "Asia/Kolkata" })}
                {today?.shift_start ? ` · Shift starts ${today.shift_start}` : " · No shift start configured"}
                {entry?.late ? " · " : ""}
                {entry?.late && <span className="font-medium text-rose-600">Late clock-in</span>}
              </div>
            </div>
          </div>
          {!entry && (
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => punch("in")} disabled={busy} data-testid="clock-in-button">
              <LogIn className="mr-2 h-4 w-4" /> Clock In
            </Button>
          )}
          {open && (
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => punch("out")} disabled={busy} data-testid="clock-out-button">
              <LogOut className="mr-2 h-4 w-4" /> Clock Out
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <CalendarDays className="h-4 w-4 text-slate-400" />
        <Label className="text-sm">Month</Label>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="attendance-month-picker" />
      </div>

      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-base">My Attendance — {month}</CardTitle></CardHeader>
        <CardContent><EntriesTable entries={myEntries} testid="my-history-table" /></CardContent>
      </Card>

      {isAdmin && (
        <>
          <Card className="border-slate-200" data-testid="admin-summary-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><Users className="h-4 w-4 text-indigo-500" /> Monthly Summary — all Firm 2 team ({summary?.elapsed_days ?? "…"} working days elapsed)</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Present</TableHead>
                    <TableHead className="text-right">Absent</TableHead>
                    <TableHead className="text-right">Late</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead className="text-right">History</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="admin-summary-table">
                  {(summary?.rows || []).map((r) => (
                    <TableRow key={r.user_id} data-testid={`summary-row-${r.user_id}`}>
                      <TableCell className="font-medium text-slate-900">{r.employee}</TableCell>
                      <TableCell className="capitalize text-slate-500">{r.role}</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-700">{r.present}</TableCell>
                      <TableCell className="text-right text-slate-600">{r.absent}</TableCell>
                      <TableCell className="text-right">{r.late > 0 ? <span className="font-semibold text-rose-600">{r.late}</span> : 0}</TableCell>
                      <TableCell className="text-right">{r.hours}h</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setEmpSel(r.user_id)} data-testid={`view-employee-${r.user_id}`}>View</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader><CardTitle className="text-base">Employee Attendance</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Select value={empSel || "none"} onValueChange={(v) => setEmpSel(v === "none" ? "" : v)}>
                <SelectTrigger className="w-72" data-testid="attendance-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select employee</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.role})</SelectItem>)}
                </SelectContent>
              </Select>
              {empSel && <EntriesTable entries={empEntries} showEmployee testid="employee-entries-table" />}
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="attendance-config-card">
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Settings2 className="h-4 w-4 text-indigo-500" /> Shift & Working-Day Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Company-wide shift window (HH:MM, IST)</Label>
                <div className="mt-1.5 flex max-w-md items-center gap-2">
                  <Input type="time" value={cfg.shift_start} onChange={(e) => setCfg({ ...cfg, shift_start: e.target.value })} data-testid="shift-start-input" />
                  <span className="text-xs text-slate-400">to</span>
                  <Input type="time" value={cfg.shift_end} onChange={(e) => setCfg({ ...cfg, shift_end: e.target.value })} data-testid="shift-end-input" />
                </div>
                <p className="mt-1 text-[11px] text-slate-400">Clock-in after start = Late. Clock-out before mid-shift (+1h grace) = HalfDay (half daily rate in payroll). Clock-out after that but before shift end = LeftEarly. Leave end empty to disable half-day/early tracking.</p>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Working days (drives Absent counts & Payroll)</Label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="working-days-picker">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => {
                    const on = cfg.working_days.includes(i);
                    return (
                      <button
                        key={d}
                        type="button"
                        data-testid={`workday-${d.toLowerCase()}`}
                        onClick={() => setCfg({ ...cfg, working_days: on ? cfg.working_days.filter((x) => x !== i) : [...cfg.working_days, i].sort() })}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${on ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-500 hover:border-indigo-300"}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
                {!cfg.working_days.includes(6) && (
                  <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
                    <Switch checked={cfg.alt_sundays} onCheckedChange={(v) => setCfg({ ...cfg, alt_sundays: v })} data-testid="alt-sundays-switch" />
                    Alternate Sundays are working (1st, 3rd, 5th of the month)
                  </label>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Holidays (excluded from working days — no absent counted)</Label>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Input type="date" value={hol.date} onChange={(e) => setHol({ ...hol, date: e.target.value })} className="w-40" data-testid="holiday-date-input" />
                  <Input placeholder="Holiday name (e.g. Diwali)" value={hol.name} onChange={(e) => setHol({ ...hol, name: e.target.value })} className="w-56" data-testid="holiday-name-input" />
                  <Button type="button" variant="outline" size="sm" onClick={addHoliday} data-testid="holiday-add-button">Add</Button>
                </div>
                {cfg.holidays.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="holiday-list">
                    {cfg.holidays.map((h) => (
                      <span key={h.date} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs" data-testid={`holiday-${h.date}`}>
                        <span className="font-medium text-slate-700">{h.name}</span>
                        <span className="text-slate-400">{h.date}</span>
                        <button type="button" onClick={() => removeHoliday(h.date)} className="text-slate-400 hover:text-rose-500" data-testid={`holiday-remove-${h.date}`} aria-label={`Remove ${h.name}`}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="mt-1 text-[11px] text-slate-400">Applies to attendance summaries and payroll after you save. e.g. Diwali, Holi, Christmas.</p>
              </div>
              <div>
                <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><MapPin className="h-3.5 w-3.5" /> Office geo-fence (block clock-in from outside)</Label>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={useMyLocation} data-testid="geo-use-current">Use my current location</Button>
                  <Input placeholder="Latitude" value={cfg.geo_lat} onChange={(e) => setCfg({ ...cfg, geo_lat: e.target.value })} className="w-32" data-testid="geo-lat-input" />
                  <Input placeholder="Longitude" value={cfg.geo_lng} onChange={(e) => setCfg({ ...cfg, geo_lng: e.target.value })} className="w-32" data-testid="geo-lng-input" />
                  <Input type="number" min="25" max="5000" value={cfg.geo_radius_m} onChange={(e) => setCfg({ ...cfg, geo_radius_m: e.target.value })} className="w-28" data-testid="geo-radius-input" />
                  <span className="text-xs text-slate-400">metres</span>
                  {(cfg.geo_lat !== "" || cfg.geo_lng !== "") && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => setCfg({ ...cfg, geo_lat: "", geo_lng: "" })} data-testid="geo-clear">Clear</Button>
                  )}
                </div>
                <p className="mt-1 text-[11px] text-slate-400">When set, everyone must be within this radius of the office point to clock in — the browser will ask for location permission. Clear the coordinates to disable.</p>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Per-employee overrides</Label>
                <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {employees.map((e) => (
                    <div key={e.id} className="rounded-md border border-slate-100 p-2.5">
                      <Label className="text-xs font-medium">{e.name}</Label>
                      <div className="mt-1.5 flex items-center gap-2">
                        <Input
                          type="time"
                          value={cfg.per_user[e.id] || ""}
                          onChange={(e2) => setCfg({ ...cfg, per_user: { ...cfg.per_user, [e.id]: e2.target.value } })}
                          data-testid={`shift-emp-${e.id}`}
                        />
                        <span className="text-[10px] text-slate-400">to</span>
                        <Input
                          type="time"
                          value={cfg.per_user_end[e.id] || ""}
                          onChange={(e2) => setCfg({ ...cfg, per_user_end: { ...cfg.per_user_end, [e.id]: e2.target.value } })}
                          data-testid={`shift-end-emp-${e.id}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={saveCfg} disabled={savingCfg} data-testid="attendance-config-save">
                {savingCfg ? "Saving..." : "Save Shift Settings"}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
